import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, Folder, Car, Phone, Mail, Search, MapPin, Star, Archive, ArchiveRestore } from 'lucide-react';
import ArchiveerAutoDialog from '../components/ArchiveerAutoDialog';
import { useData } from '../context/DataContext';
import { toast } from 'sonner';
import { makeDirtyClose } from '../lib/useDirtyClose';
import { carsysApi } from '../api/carsysApi';
import AutoEditor, { voertuigTypeMeta } from '../components/AutoEditor';
import PlaatsAutocomplete from '../components/PlaatsAutocomplete';
import PhoneInput, { formatPhoneDisplay } from '../components/PhoneInput';
import { titleCaseName } from '../lib/casing';
import { isApkExempt } from '../lib/apk';

import { useT } from '../context/LanguageContext';
import { confirmDialog } from '../lib/confirm';
export default function Klanten({ onNavigate }) {

  const { t } = useT();  const { klanten: initial, klantAutos: alleAutos, bonnen, createKlant, updateKlant, deleteKlant, deleteKlantAuto, setPrimaryKlantAuto, herstelKlantAuto, adresLookup } = useData();
  const klantAutos = useMemo(() => (alleAutos || []).filter(a => !a.gearchiveerd), [alleAutos]);
  const [archiveerAuto, setArchiveerAuto] = useState(null);
  const archiefKt = useMemo(() => new Set((alleAutos || []).filter(a => a.gearchiveerd).map(a => a.kenteken)), [alleAutos]);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const editingInit = useRef(null);
  useEffect(() => {
    if (editing && editingInit.current === null) editingInit.current = JSON.stringify(editing);
    if (!editing) editingInit.current = null;
  }, [editing]);
  const requestCloseKlant = makeDirtyClose(
    () => !!editing && editingInit.current !== null && JSON.stringify(editing) !== editingInit.current,
    () => setEditing(null), t
  );
  const [autoEditor, setAutoEditor] = useState(null); // { auto: {...} } or { klantId: '...' } for new
  const list = initial;

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return list;
    return list.filter(k =>
      k.naam.toLowerCase().includes(f) ||
      k.plaats.toLowerCase().includes(f) ||
      k.telefoon.includes(f) ||
      k.email.toLowerCase().includes(f) ||
      (k.kentekens || []).some(kt => kt.toLowerCase().includes(f))
    );
  }, [list, filter]);

  const selected = list.find(k => k.id === selectedId) || list[0];
  const autosVanKlant = useMemo(() => {
    if (!selected) return [];
    // Sorteer: hoofdvoertuig eerst, dan op kenteken
    return klantAutos
      .filter(a => a.klantId === selected.id)
      .sort((a, b) => (b.isHoofd ? 1 : 0) - (a.isHoofd ? 1 : 0) || (a.kenteken || '').localeCompare(b.kenteken || ''));
  }, [selected, klantAutos]);

  const archiefVanKlant = useMemo(() => (selected ? (alleAutos || []).filter(a => a.klantId === selected.id && a.gearchiveerd) : []), [selected, alleAutos]);
  const bonCount = (kt) => (bonnen || []).filter(b => (b.kenteken || '').toUpperCase() === kt).length;

  const handleNew = () => {
    setEditing({ naam: '', telefoon: '', email: '', adres: '', postcode: '', plaats: '', status: 'Actief', notitie: '', kentekens: [], _isNew: true });
  };

  const handleEdit = () => {
    if (selected) setEditing({ ...selected });
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!(await confirmDialog(`Klant '${selected.naam}' verwijderen?`))) return;
    try {
      await deleteKlant(selected.id);
      toast.success(t('Klant verwijderd'));
      setSelectedId(null);
    } catch (e) { toast.error(t('Verwijderen mislukt')); }
  };

  const handleSave = async () => {
    if (!editing.naam.trim()) { toast.error(t('Naam is verplicht')); return; }
    try {
      if (editing._isNew) {
        const { _isNew, ...payload } = editing;
        const created = await createKlant(payload);
        setSelectedId(created.id);
      } else {
        const { _isNew, ...payload } = editing;
        await updateKlant(editing.id, payload);
      }
      toast.success(t('Klant opgeslagen'));
      setEditing(null);
    } catch (e) { toast.error(t('Opslaan mislukt')); }
  };

  return (
    <div>
      <div className="cs-page-header">
        <div><h2>{t('page.customers.title')}</h2><p>{list.length} {t('klanten geregistreerd', 'klanten geregistreerd')}</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="cs-btn primary" onClick={handleNew}><Plus size={14} /> {t('Nieuwe klant', 'Nieuwe klant')}</button>
          <button className="cs-btn" onClick={handleEdit}><Edit size={14} /> {t('Bewerken')}</button>
          <button className="cs-btn danger" onClick={handleDelete}><Trash2 size={14} /> {t('Verwijderen')}</button>
          <button className="cs-btn" disabled={!selected} title={selected ? `Open documenten van ${selected.naam}` : 'Selecteer eerst een klant'} onClick={() => {
            if (!selected || !onNavigate) return;
            sessionStorage.setItem('documenten:filterKlantId', selected.id);
            sessionStorage.setItem('documenten:filterKlantNaam', selected.naam || '');
            onNavigate('documenten');
          }} data-testid="btn-klant-open-folder"><Folder size={14} /> {t('Map openen', 'Map openen')}</button>
        </div>
      </div>

      <div className="cs-toolbar">
        <div className="grow" style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--cs-text-muted)' }} />
          <input className="cs-input" style={{ paddingLeft: 30 }} placeholder={t('Zoek op naam, plaats, telefoon, kenteken...', 'Zoek op naam, plaats, telefoon, kenteken...')} value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
      </div>

      <div className="cs-master-detail">
        <div className="cs-card">
          <table className="cs-table">
            <thead><tr><th>{t('Naam')}</th><th>{t('Plaats')}</th><th>{t('Telefoon')}</th><th>{t('Status')}</th><th>{t('Kentekens')}</th></tr></thead>
            <tbody>
              {filtered.map(k => (
                <tr key={k.id} className={k.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(k.id)} onDoubleClick={() => setEditing({ ...k })} style={{ cursor: 'pointer' }} title={t('Dubbelklik om te bewerken', 'Dubbelklik om te bewerken')}>
                  <td data-label={t('Naam')} style={{ fontWeight: 600 }}>{k.naam}</td>
                  <td data-label={t('Plaats')}>{k.plaats}</td>
                  <td data-label={t('Telefoon')}>{formatPhoneDisplay(k.telefoon)}</td>
                  <td data-label={t('Status')}><span className={`cs-badge ${k.status === 'Slapend' ? 'gray' : k.status === 'Zakelijk' ? 'blue' : k.status === 'Gearchiveerd' ? 'amber' : 'green'}`}>{k.status}</span></td>
                  <td data-label={t('Kentekens')} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{(k.kentekens || []).filter(kt => !archiefKt.has(kt)).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="cs-empty">{t('Geen klanten gevonden.', 'Geen klanten gevonden.')}</div>}
        </div>

        <div className="cs-card padded">
          {selected ? (
            <>
              <h3 className="cs-card-title"><Users size={16} /> {selected.naam}</h3>
              <div className="cs-detail-row"><span className="lbl">{t('Telefoon')}</span><span className="val"><Phone size={12} style={{ verticalAlign: 'middle' }} /> {formatPhoneDisplay(selected.telefoon) || '-'}{selected.telefoon && <a href={`tel:${selected.telefoon}`} style={{ marginLeft: 8, fontSize: 11 }}>{t('bellen', 'bellen')}</a>}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('E-mail')}</span><span className="val"><Mail size={12} style={{ verticalAlign: 'middle' }} /> {selected.email}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Adres')}</span><span className="val">{selected.adres}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Postcode / Plaats', 'Postcode / Plaats')}</span><span className="val">{selected.postcode}  {selected.plaats}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Status')}</span><span className="val">{selected.status}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Notitie')}</span><span className="val">{selected.notitie || '-'}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Laatst gezien', 'Laatst gezien')}</span><span className="val">{selected.laatstGezien ? new Date(selected.laatstGezien).toLocaleDateString('nl-NL') : '-'}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Laatst gefactureerd')}</span><span className="val">{selected.laatstGefactureerd ? new Date(selected.laatstGefactureerd).toLocaleDateString('nl-NL') : '-'}</span></div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '14px 0 6px' }}>
                <h4 style={{ margin: 0, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5 }}><Car size={14} style={{ display: 'inline' }} /> {t('Voertuigen')} ({autosVanKlant.length})</h4>
                <button data-testid="btn-auto-toevoegen" className="cs-btn ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => setAutoEditor({ klantId: selected.id })}><Plus size={11} /> {t('Voertuig toevoegen', 'Voertuig toevoegen')}</button>
              </div>
              {autosVanKlant.length === 0 ? <div style={{ fontSize: 12, color: 'var(--cs-text-muted)' }}>{t("Geen voertuigen geregistreerd. Klik 'Voertuig toevoegen' hierboven.", "Geen voertuigen geregistreerd. Klik 'Voertuig toevoegen' hierboven.")}</div> : (
                <table className="cs-table" style={{ marginTop: 6 }}>
                  <thead><tr><th></th><th>{t('common.type', 'Type')}</th><th>{t('col.kenteken', 'Kenteken')}</th><th>{t('col.merk_model', 'Merk / Model')}</th><th>{t('col.km', 'KM')}</th><th>{t('col.apk', 'APK')}</th><th></th></tr></thead>
                  <tbody>
                    {autosVanKlant.map(a => {
                      const tm = voertuigTypeMeta(a.voertuigType);
                      const isMain = autosVanKlant.length > 1 && !!a.isHoofd;
                      return (
                        <tr key={a.kenteken} onDoubleClick={() => setAutoEditor({ auto: { ...a, _existing: true } })} style={isMain ? { background: 'rgba(250, 204, 21, 0.08)' } : {}}>
                          <td style={{ width: 20 }}>
                            {isMain
                              ? <span title={t('Hoofdvoertuig')} style={{ color: '#eab308' }}><Star size={13} fill="#eab308" stroke="#eab308" /></span>
                              : (autosVanKlant.length > 1 && <button className="cs-btn ghost" style={{ padding: 2, color: 'var(--cs-text-muted)' }} title={t('Maak hoofdvoertuig', 'Maak hoofdvoertuig')} onClick={async () => {
                                  try { await setPrimaryKlantAuto(a.kenteken); toast.success(t('Hoofdvoertuig ingesteld', `Hoofdvoertuig: ${a.kenteken}`)); }
                                  catch (e) { toast.error(t('Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
                                }}><Star size={11} /></button>)
                            }
                          </td>
                          <td title={tm.label} style={{ fontSize: 18 }}>{tm.icon}</td>
                          <td style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{a.kenteken}{isMain && <span style={{ marginLeft: 6, fontSize: 10, color: '#a16207', fontWeight: 700 }}>{t('HOOFD', 'HOOFD')}</span>}</td>
                          <td>{a.merk} {a.model}</td>
                          <td>{(a.kmStand || 0).toLocaleString('nl-NL')}</td>
                          <td>{isApkExempt(a) ? <span style={{ color: 'var(--cs-text-muted)', fontSize: 11.5 }} title={t('Voertuig vrijgesteld van APK', 'Voertuig vrijgesteld van APK')}>{t('N.v.t.', 'N.v.t.')}</span> : (a.apkVervalt ? new Date(a.apkVervalt).toLocaleDateString('nl-NL') : '-')}</td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <button className="cs-btn ghost" style={{ padding: 4 }} title={t('Bewerken')} onClick={() => setAutoEditor({ auto: { ...a, _existing: true } })}><Edit size={11} /></button>
                            <button className="cs-btn ghost" style={{ padding: 4 }} title={t('Archiveren (klant heeft auto niet meer)')} onClick={() => setArchiveerAuto(a)} data-testid={`klant-auto-archiveer-${a.kenteken}`}><Archive size={11} /></button>
                            <button className="cs-btn ghost danger" style={{ padding: 4 }} title={t('Verwijderen')} onClick={async () => {
                              if (!(await confirmDialog(`${t('Voertuig')} ${a.kenteken} ${t('verwijderen?')}`))) return;
                              try { await deleteKlantAuto(a.kenteken); toast.success(t('Voertuig verwijderd')); }
                              catch { toast.error(t('Mislukt')); }
                            }}><Trash2 size={11} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {archiefVanKlant.length > 0 && (
                <details style={{ marginTop: 12 }} data-testid="klant-autos-archief">
                  <summary style={{ cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--cs-text-muted)' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Archive size={12} style={{ display: 'inline' }} /> {t('Gearchiveerd')} ({archiefVanKlant.length})</span></summary>
                  <table className="cs-table" style={{ marginTop: 6, opacity: 0.85 }}>
                    <thead><tr><th>{t('col.kenteken', 'Kenteken')}</th><th>{t('col.merk_model', 'Merk / Model')}</th><th>{t('Reden')}</th><th>{t('Sinds')}</th><th>{t('Bonnen')}</th><th></th></tr></thead>
                    <tbody>
                      {archiefVanKlant.map(a => (
                        <tr key={a.kenteken} onDoubleClick={() => setAutoEditor({ auto: { ...a, _existing: true } })} data-testid={`klant-auto-archief-${a.kenteken}`}>
                          <td style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{a.kenteken}</td>
                          <td>{a.merk} {a.model}</td>
                          <td title={a.archiefToelichting || ''}><span className="cs-badge gray">{t(a.archiefReden || 'Overig')}</span></td>
                          <td>{a.gearchiveerdOp ? new Date(a.gearchiveerdOp).toLocaleDateString('nl-NL') : '-'}</td>
                          <td>{bonCount(a.kenteken)}</td>
                          <td style={{ textAlign: 'right' }}>
                            <button className="cs-btn ghost" style={{ padding: '3px 6px', fontSize: 11 }} title={t('Terugzetten naar actief')} data-testid={`klant-auto-herstel-${a.kenteken}`} onClick={async () => {
                              try { await herstelKlantAuto(a.kenteken); toast.success(t('Voertuig teruggezet'), { description: a.kenteken }); }
                              catch (e) { toast.error(t('Mislukt'), { description: e.response?.data?.detail || e.message }); }
                            }}><ArchiveRestore size={11} /> {t('Terugzetten')}</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </>
          ) : <div className="cs-empty">{t('Selecteer een klant.', 'Selecteer een klant.')}</div>}
        </div>
      </div>

      {editing && (
        <div className="cs-modal-overlay" onClick={requestCloseKlant}>
          <div className="cs-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{editing._isNew ? t('Nieuwe klant') : t('Klant bewerken')}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><label className="cs-section-title">{t('Naam')} *</label><input className="cs-input" value={editing.naam} onChange={e => setEditing({ ...editing, naam: e.target.value })} onBlur={e => setEditing({ ...editing, naam: titleCaseName(e.target.value) })} /></div>
              <div><label className="cs-section-title">{t('Status')}</label>
                <select className="cs-input" value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })} data-testid="klant-status">
                  <option value="Actief">{t('Actief', 'Actief')}</option>
                  <option value="Slapend">{t('Slapend', 'Slapend')}</option>
                  <option value="Zakelijk">{t('Zakelijk', 'Zakelijk')}</option>
                  <option value="Gearchiveerd">{t('Gearchiveerd', 'Gearchiveerd')}</option>
                </select>
              </div>
              <div><label className="cs-section-title">{t('Telefoon')}</label><PhoneInput value={editing.telefoon} onChange={(v) => setEditing({ ...editing, telefoon: v })} testid="klant-telefoon" /></div>
              <div><label className="cs-section-title">{t('E-mail')}</label><input className="cs-input" value={editing.email} onChange={e => setEditing({ ...editing, email: e.target.value })} /></div>
              <div style={{ gridColumn: '1 / 3' }}>
                <label className="cs-section-title">{t('Adres (straat + huisnummer)')}</label>
                <input className="cs-input" value={editing.adres} onChange={e => setEditing({ ...editing, adres: e.target.value })} placeholder={t('bv. Bagijnhof 33 of Kerkstraat 5')} style={{ marginBottom: 6 }} />
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button className="cs-btn" type="button" title={t('Met postcode + huisnummer vullen we straat en plaats in')} onClick={async () => {
                    if (!editing.postcode || !editing.adres) { toast.error(t('Vul eerst postcode + huisnr in')); return; }
                    const hnr = (editing.adres.match(/\d+/) || [''])[0];
                    if (!hnr) { toast.error(t('Geen huisnummer gevonden in adresveld')); return; }
                    try {
                      const a = await adresLookup(editing.postcode, hnr);
                      setEditing({ ...editing, adres: `${a.straat} ${hnr}`, plaats: a.plaats, postcode: a.postcode });
                      toast.success(t('Adres gevonden: ') + a.weergavenaam);
                    } catch (e) { toast.error(t('PDOK lookup mislukt'), { description: e.response?.data?.detail || '' }); }
                  }}><MapPin size={12} /> {t('Vul in via postcode', 'Vul in via postcode')}</button>
                  <button className="cs-btn" type="button" title={t('Met straat + huisnummer + plaats vinden we de postcode')} onClick={async () => {
                    if (!editing.adres) { toast.error(t('Vul eerst straat + huisnr in (bv. "Bagijnhof 33")')); return; }
                    if (!editing.plaats) { toast.error(t('Vul ook plaats in (autocomplete helpt)')); return; }
                    const m = editing.adres.match(/^(.+?)\s+(\d+\S*)\s*$/);
                    if (!m) { toast.error(t('Kan straat + huisnummer niet lezen uit "') + editing.adres + '"'); return; }
                    try {
                      const a = await carsysApi.adresLookupByStraat(m[1].trim(), m[2].trim(), editing.plaats);
                      setEditing({ ...editing, adres: `${a.straat} ${a.huisnummer}`, plaats: a.plaats, postcode: a.postcode });
                      toast.success(`${t('Gevonden:')} ${a.weergavenaam}`);
                    } catch (e) { toast.error(t('Adres niet gevonden'), { description: e.response?.data?.detail || '' }); }
                  }}><MapPin size={12} /> {t('Vul postcode via straat + plaats', 'Vul postcode via straat + plaats')}</button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--cs-text-muted)', marginTop: 6 }}>
                  {t('Tip: heb je geen postcode? Vul straat + huisnr in en kies plaats hieronder (typ "Med" voor Medemblik). Klik dan op "Vul postcode via straat + plaats".')}
                </div>
              </div>
              <div><label className="cs-section-title">{t('Postcode')}</label><input className="cs-input" value={editing.postcode} onChange={e => setEditing({ ...editing, postcode: e.target.value })} placeholder="1011 AA" /></div>
              <div><label className="cs-section-title">{t('Plaats')}</label>
                <PlaatsAutocomplete value={editing.plaats} onChange={(v) => setEditing({ ...editing, plaats: v })} placeholder={t('bv. Medemblik')} testid="klant-plaats" />
              </div>
              <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Notitie')}</label><textarea className="cs-input" rows={3} value={editing.notitie} onChange={e => setEditing({ ...editing, notitie: e.target.value })} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="cs-btn" onClick={requestCloseKlant}>{t('Annuleren')}</button>
              <button className="cs-btn primary" onClick={handleSave}>{t('Opslaan')}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .cs-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; animation: fadeIn 140ms ease; }
        .cs-modal { background: #fff; border-radius: 10px; padding: 20px 22px; width: 560px; max-width: 92vw; box-shadow: 0 20px 60px rgba(0,0,0,0.25); animation: fadeUp 220ms ease; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      {archiveerAuto && <ArchiveerAutoDialog auto={archiveerAuto} onClose={() => setArchiveerAuto(null)} />}
      {autoEditor && <AutoEditor auto={autoEditor.auto} klantId={autoEditor.klantId || autoEditor.auto?.klantId} onClose={() => setAutoEditor(null)} />}
    </div>
  );
}
